/** Weergavelogica voor bekendmakingen, los van React zodat hij te testen is. */

type MetVlag = { vergunning: boolean };

/**
 * Twee lijsten, want het zijn twee soorten nieuws: beleid zegt wat er mag gaan
 * gelden, een vergunning zegt wat er werkelijk gebeurt. Door elkaar gezet
 * verliest het beleid het altijd — dat hangt aan een gemeente en de vergunning
 * aan een adres, dus op afstand staat de dakkapel bovenaan.
 *
 * De vlag komt van de monitor: welke soorten een vergunning zijn hoort bij de
 * bron, niet bij de frontend.
 */
export const splits = <T extends MetVlag>(publicaties: T[]) => ({
  beleid: publicaties.filter((p) => !p.vergunning),
  vergunningen: publicaties.filter((p) => p.vergunning),
});

/** "12 september 2026" — bekendmakingen worden op datum besproken. */
export const datum = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("nl-NL", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

/** "13 mrt 2026" — voor de datumkolom van de tabel. */
export const datumKort = (iso: string | null) =>
  iso
    ? new Date(iso)
        .toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" })
        .replace(".", "")
    : "";

export type Uitkomst = "open" | "positief" | "negatief";

const UITKOMSTEN: Record<string, Uitkomst> = {
  aanvraag: "open",
  termijn_verlengd: "open",
  ontwerp: "open",
  verleend: "positief",
  vastgesteld: "positief",
  geweigerd: "negatief",
  ingetrokken: "negatief",
};

/**
 * De stand van een procedure, teruggebracht tot wat een icoon kan zeggen:
 * loopt nog, pakte goed uit, of niet. De status zelf komt van de monitor
 * (`PROCEDURESTATUSSEN`, gevraagd in omgevingsmonitor#85); `onbekend`, een
 * ontbrekend veld of een waarde die we niet kennen geeft geen icoon.
 */
export const uitkomst = (procedurestatus?: string): Uitkomst | null =>
  (procedurestatus && UITKOMSTEN[procedurestatus]) || null;

/**
 * De typekolom. Nu de soort van de bron ("omgevingsvergunning",
 * "participatie"); zodra de monitor een ontwikkeltype per publicatie levert
 * (plan publicatieclassificatie, fase 2), is dit de ene plek die overstapt.
 */
export const typeLabel = (p: { soort: string }) => p.soort;

/**
 * Afstand pas noemen als hij iets toevoegt: "op het perceel" is informatief,
 * "op 3 meter" suggereert een precisie die de bron niet heeft — het is het
 * gepubliceerde gebied, niet de exacte plek van de ingreep.
 */
const OP_HET_PERCEEL = 25;

export const afstand = (meter: number | null) => {
  if (meter === null) return "";
  if (meter < OP_HET_PERCEEL) return "op het perceel";
  if (meter < 1000) return `${Math.round(meter / 10) * 10} m`;
  return `${(meter / 1000).toFixed(1)} km`;
};

/** Dezelfde afstand voor de smalle tabelkolom: op het perceel zelf een streepje. */
export const afstandKort = (meter: number | null) =>
  meter !== null && meter < OP_HET_PERCEEL ? "-" : afstand(meter);
